import React, { useState } from 'react';
import { RegistroFisicoEvolucion } from '@/services/EstadisticasService';
import { TrendingUp, Info } from 'lucide-react';

interface GraficaEvolucionFisicaProps {
  registros: RegistroFisicoEvolucion[];
}

/**
 * Componente para mostrar gráficas de evolución física (altura, peso, IMC)
 */
export const GraficaEvolucionFisica: React.FC<GraficaEvolucionFisicaProps> = ({ registros }) => {
  const [mostrarLeyenda, setMostrarLeyenda] = useState(false);

  if (!registros || registros.length === 0) {
    return (
      <div className="text-center py-8">
        <TrendingUp className="w-12 h-12 text-slate-500 mb-3 mx-auto" />
        <p className="text-slate-400 font-poppins">
          No hay datos suficientes para mostrar gráficas
        </p>
        <p className="text-slate-500 font-poppins text-sm mt-1">
          Se necesitan al menos 2 registros físicos
        </p>
      </div>
    );
  }

  // Ordenar datos por fecha
  const datosOrdenados = [...registros].sort((a, b) => new Date(a.fecha).getTime() - new Date(b.fecha).getTime());
  
  // Calcular valores mínimos y máximos para las escalas
  const alturas = datosOrdenados.map(d => d.altura);
  const pesos = datosOrdenados.map(d => d.peso);
  const imcs = datosOrdenados.map(d => d.imc);
  
  const minAltura = Math.min(...alturas) - 5;
  const maxAltura = Math.max(...alturas) + 5;
  const minPeso = Math.min(...pesos) - 5;
  const maxPeso = Math.max(...pesos) + 5;
  const minIMC = Math.min(...imcs) - 2;
  const maxIMC = Math.max(...imcs) + 2;

  /**
   * Convierte un valor a posición en el gráfico (0-100%)
   */
  const valorAPosicion = (valor: number, min: number, max: number): number => {
    return ((valor - min) / (max - min)) * 100;
  };

  /**
   * Formatea la fecha para mostrar en las etiquetas
   */
  const formatearFecha = (fecha: string): string => {
    return new Date(fecha).toLocaleDateString('es-ES', { 
      day: '2-digit', 
      month: '2-digit',
      year: '2-digit'
    });
  };

  /**
   * Obtiene el color según el rango de IMC
   */
  const obtenerColorIMC = (imc: number): string => {
    if (imc < 18.5) return 'text-blue-400';
    if (imc >= 18.5 && imc < 25) return 'text-green-400';
    if (imc >= 25 && imc < 30) return 'text-yellow-400';
    return 'text-red-400';
  };

  /**
   * Alterna la visibilidad de la leyenda del IMC
   */
  const toggleLeyenda = () => {
    setMostrarLeyenda(!mostrarLeyenda);
  };

  return (
    <div className="space-y-6">
      {/* Gráfica de Altura */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-slate-100 mb-4 font-poppins">
          Evolución de Altura
        </h4>
        <div className="relative h-32 bg-slate-900/50 rounded-lg p-4">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Línea de tendencia */}
            <polyline
              fill="none"
              stroke="#10b981"
              strokeWidth="2"
              points={datosOrdenados.map((dato, index) => 
                `${(index / (datosOrdenados.length - 1)) * 100},${100 - valorAPosicion(dato.altura, minAltura, maxAltura)}`
              ).join(' ')}
            />
            {/* Puntos */}
            {datosOrdenados.map((dato, index) => (
              <circle
                key={index}
                cx={(index / (datosOrdenados.length - 1)) * 100}
                cy={100 - valorAPosicion(dato.altura, minAltura, maxAltura)}
                r="2"
                fill="#10b981"
              />
            ))}
          </svg>
          {/* Etiquetas de valores */}
          <div className="absolute inset-0 flex items-end justify-between px-2 pb-1">
            {datosOrdenados.map((dato, index) => (
              <div key={index} className="text-xs text-slate-400 text-center">
                <div>{dato.altura}cm</div>
                <div>{formatearFecha(dato.fecha)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gráfica de Peso */}
      <div className="bg-slate-800/50 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-slate-100 mb-4 font-poppins">
          Evolución de Peso
        </h4>
        <div className="relative h-32 bg-slate-900/50 rounded-lg p-4">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            {/* Línea de tendencia */}
            <polyline
              fill="none"
              stroke="#3b82f6"
              strokeWidth="2"
              points={datosOrdenados.map((dato, index) => 
                `${(index / (datosOrdenados.length - 1)) * 100},${100 - valorAPosicion(dato.peso, minPeso, maxPeso)}`
              ).join(' ')}
            />
            {/* Puntos */}
            {datosOrdenados.map((dato, index) => (
              <circle
                key={index}
                cx={(index / (datosOrdenados.length - 1)) * 100}
                cy={100 - valorAPosicion(dato.peso, minPeso, maxPeso)}
                r="2"
                fill="#3b82f6"
              />
            ))}
          </svg>
          {/* Etiquetas de valores */}
          <div className="absolute inset-0 flex items-end justify-between px-2 pb-1">
            {datosOrdenados.map((dato, index) => (
              <div key={index} className="text-xs text-slate-400 text-center">
                <div>{dato.peso}kg</div>
                <div>{formatearFecha(dato.fecha)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Gráfica de IMC */}      
        <div className="bg-slate-800/50 rounded-lg p-4">
          <div className="flex items-center justify-between mb-4">
            <h4 className="text-lg font-semibold text-slate-100 font-poppins">
              Evolución de IMC
            </h4>
            <button
            onClick={toggleLeyenda}
            className="p-1 rounded-full hover:bg-slate-700/50 transition-colors duration-200"
            title={mostrarLeyenda ? "Ocultar leyenda" : "Mostrar leyenda"}
            >
              <Info className={`w-4 h-4 transition-colors duration-200 ${
              mostrarLeyenda ? 'text-emerald-400' : 'text-slate-400 hover:text-slate-300'
              }`} />
            </button>
          </div>
          
          <div className="relative h-32 bg-slate-900/50 rounded-lg p-4">
            <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
              {/* Líneas de referencia IMC */}
              <line x1="0" y1={100 - valorAPosicion(18.5, minIMC, maxIMC)} x2="100" y2={100 - valorAPosicion(18.5, minIMC, maxIMC)} stroke="#64748b" strokeWidth="0.5" strokeDasharray="2,2" />
              <line x1="0" y1={100 - valorAPosicion(25, minIMC, maxIMC)} x2="100" y2={100 - valorAPosicion(25, minIMC, maxIMC)} stroke="#64748b" strokeWidth="0.5" strokeDasharray="2,2" />
              <line x1="0" y1={100 - valorAPosicion(30, minIMC, maxIMC)} x2="100" y2={100 - valorAPosicion(30, minIMC, maxIMC)} stroke="#64748b" strokeWidth="0.5" strokeDasharray="2,2" />
              
              {/* Línea de tendencia */}
              <polyline
                fill="none"
                stroke="#f59e0b"
                strokeWidth="2"
                points={datosOrdenados.map((dato, index) => 
                  `${(index / (datosOrdenados.length - 1)) * 100},${100 - valorAPosicion(dato.imc, minIMC, maxIMC)}`
                ).join(' ')}
              />
              {/* Puntos */}
              {datosOrdenados.map((dato, index) => (
                <circle
                  key={index}
                  cx={(index / (datosOrdenados.length - 1)) * 100}
                  cy={100 - valorAPosicion(dato.imc, minIMC, maxIMC)}
                  r="2"
                  fill="#f59e0b"
                />
              ))}
            </svg>
            {/* Etiquetas de valores */}
            <div className="absolute inset-0 flex items-end justify-between px-2 pb-1">
              {datosOrdenados.map((dato, index) => (
                <div key={index} className="text-xs text-center">
                  <div className={obtenerColorIMC(dato.imc)}>
                    {dato.imc.toFixed(1)}
                  </div>
                  <div className="text-slate-400">{formatearFecha(dato.fecha)}</div>
                </div>
              ))}
            </div>
          </div>
          
          {/* Leyenda de IMC - Solo mostrar si mostrarLeyenda es true */}
          {mostrarLeyenda && (
            <div className="mt-4 p-3 bg-slate-900/50 rounded-lg">
              <div className="flex items-center gap-2 mb-2">
                <Info className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-medium text-slate-300 font-poppins">Rangos de IMC</span>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-blue-400 rounded-full"></div>
                  <span className="text-slate-400">Bajo peso: &lt; 18.5</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-green-400 rounded-full"></div>
                  <span className="text-slate-400">Normal: 18.5 - 24.9</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-yellow-400 rounded-full"></div>
                  <span className="text-slate-400">Sobrepeso: 25 - 29.9</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 bg-red-400 rounded-full"></div>
                  <span className="text-slate-400">Obesidad: ≥ 30</span>
                </div>
              </div>
            </div>
          )}
        </div>
    </div>
  );
};

export default GraficaEvolucionFisica;